import React, { useState, useEffect } from 'react';
import {
  Bell, Send, Trash2, Check, CheckCheck,
  Loader,
  X, Calendar, Filter, RefreshCw, Search,
  CreditCard, Store, Tag, ShieldAlert
} from 'lucide-react';
import adminService, {
  AdminNotification,
  AdminNotificationListResponse,
  NotificationStats,
  AdminBroadcastNotification,
  SendNotificationRequest,
  ScheduleNotificationRequest,
  NotificationType
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'my-notifications' | 'send';

const NotificationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('my-notifications');
  const [loading, setLoading] = useState(false);

  // My Notifications State
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [notificationStats, setNotificationStats] = useState<NotificationStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filterType, setFilterType] = useState<NotificationType>('all');

  // Send Notification State
  const [sendForm, setSendForm] = useState<SendNotificationRequest>({
    title: '',
    message: '',
    notification_for: 'All',
    user_ids: [],
    business_ids: []
  });

  // Schedule Notification State
  const [scheduleForm, setScheduleForm] = useState<ScheduleNotificationRequest>({
    title: '',
    message: '',
    schedule_at: '',
    notification_for: 'All'
  });


  // Send Tab State - Sent Notifications Table
  const [sentNotifications, setSentNotifications] = useState<AdminBroadcastNotification[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'scheduled'>('all');
  const [recipientFilter, setRecipientFilter] = useState<'all' | 'All' | 'User' | 'Business'>('all');
  const [sentPagination, setSentPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [showSendModal, setShowSendModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [sendStats, setSendStats] = useState({
    total_sent: 0,
    total_scheduled: 0,
    total_failed: 0,
    total_notifications: 0
  });

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (activeTab === 'my-notifications') {
      fetchNotifications();
      fetchNotificationStats();
    } else if (activeTab === 'send') {
      fetchSentNotifications();
      fetchSendStats();
    }
  }, [activeTab, filterType, pagination.page, sentPagination.page, statusFilter, recipientFilter, searchQuery]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response: AdminNotificationListResponse = await adminService.getAdminNotifications({
        page: pagination.page,
        limit: pagination.limit,
        type: filterType
      });

      console.log('Fetched notifications response:', response);
      console.log('Notifications array:', response?.notifications);
      console.log('Notifications count:', response?.notifications?.length);

      // Ensure we have valid data
      if (response && response.notifications && Array.isArray(response.notifications)) {
        console.log('Setting notifications:', response.notifications.length, 'items');
        setNotifications(response.notifications);
        setPagination(response.pagination || {
          page: pagination.page,
          limit: pagination.limit,
          total: response.notifications.length,
          totalPages: 1
        });
      } else {
        console.warn('Invalid response structure:', response);
        setNotifications([]);
        setPagination({ ...pagination, total: 0, totalPages: 1 });
      }
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to fetch notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationStats = async () => {
    try {
      const stats = await adminService.getNotificationStats();
      setNotificationStats(stats);
    } catch (error: any) {
      console.error('Failed to fetch notification stats:', error);
    }
  };


  const fetchSentNotifications = async () => {
    try {
      setLoading(true);
      const allNotifications = await adminService.getBroadcastNotifications();

      // Filter by status
      let filtered = allNotifications;
      if (statusFilter === 'sent') {
        filtered = filtered.filter(n => n.status === 'sent');
      } else if (statusFilter === 'scheduled') {
        filtered = filtered.filter(n => n.status === 'scheduled');
      }
      // If statusFilter is 'all', show all notifications

      // Apply recipient filter
      if (recipientFilter !== 'all') {
        filtered = filtered.filter(n => n.notification_for === recipientFilter);
      }

      // Apply search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(n =>
          n.title.toLowerCase().includes(query) ||
          n.message.toLowerCase().includes(query)
        );
      }

      // Sort by created_at (newest first)
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Calculate pagination
      const total = filtered.length;
      const totalPages = Math.ceil(total / sentPagination.limit);
      const startIndex = (sentPagination.page - 1) * sentPagination.limit;
      const endIndex = startIndex + sentPagination.limit;
      const paginatedNotifications = filtered.slice(startIndex, endIndex);

      setSentNotifications(paginatedNotifications);
      setSentPagination({ ...sentPagination, total, totalPages });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchSendStats = async () => {
    try {
      const allNotifications = await adminService.getBroadcastNotifications();
      const sent = allNotifications.filter(n => n.status === 'sent');
      const scheduled = allNotifications.filter(n => n.status === 'scheduled');

      const totalSent = sent.reduce((sum, n) => sum + n.sent_count, 0);
      const totalFailed = sent.reduce((sum, n) => sum + n.failed_count, 0);

      setSendStats({
        total_sent: totalSent,
        total_scheduled: scheduled.length,
        total_failed: totalFailed,
        total_notifications: allNotifications.length
      });
    } catch (error: any) {
      console.error('Failed to fetch send stats:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await adminService.markNotificationRead(notificationId);
      toast.success('Notification marked as read');
      fetchNotifications();
      fetchNotificationStats();
      // Notify header to update unread count
      window.dispatchEvent(new Event('notificationUpdated'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await adminService.markAllNotificationsRead();
      toast.success(`${result.notifications_marked} notifications marked as read`);
      fetchNotifications();
      fetchNotificationStats();
      // Notify header to update unread count
      window.dispatchEvent(new Event('notificationUpdated'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  };

  const handleDeleteNotification = (notificationId: number) => {
    setNotificationToDelete(notificationId);
    setShowDeleteModal(true);
  };

  const confirmDeleteNotification = async () => {
    if (!notificationToDelete) return;

    try {
      await adminService.deleteNotification(notificationToDelete);
      toast.success('Notification deleted successfully');
      fetchNotifications();
      fetchNotificationStats();
      setShowDeleteModal(false);
      setNotificationToDelete(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    }
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const result = await adminService.sendNotification(sendForm);
      toast.success(`Notification sent successfully! Total sent: ${result.total_sent}`);
      setSendForm({
        title: '',
        message: '',
        notification_for: 'All',
        user_ids: [],
        business_ids: []
      });
      setShowSendModal(false);
      fetchSentNotifications();
      fetchSendStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Convert datetime-local (local time) to backend format
      // datetime-local gives: "2024-12-26T16:30" (user's local time, no timezone)
      // Backend expects: "YYYY-MM-DD HH:mm:ss" format
      // Since backend stores in UTC and default timezone is Asia/Kolkata,
      // we need to send the local time properly
      let scheduleDateTime = scheduleForm.schedule_at;
      if (scheduleDateTime) {
        // datetime-local value is in user's local timezone (browser timezone)
        // Create Date object from local time
        const localDate = new Date(scheduleDateTime);

        // Get local time components (user's browser timezone)
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        const hours = String(localDate.getHours()).padStart(2, '0');
        const minutes = String(localDate.getMinutes()).padStart(2, '0');
        const seconds = '00';

        // Format as "YYYY-MM-DD HH:mm:ss" (local time)
        // Backend will interpret this as local time (Asia/Kolkata) and convert to UTC
        scheduleDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }

      await adminService.scheduleNotification({
        ...scheduleForm,
        schedule_at: scheduleDateTime
      });

      toast.success('Notification scheduled successfully');
      setScheduleForm({
        title: '',
        message: '',
        schedule_at: '',
        notification_for: 'All'
      });
      setShowScheduleModal(false);
      fetchSendStats();
      fetchSentNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to schedule notification');
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString: string) => {
    if (!dateString) return '';

    try {
      // Backend stores in UTC but response comes in local time (Asia/Kolkata)
      // Format: "2024-12-26 16:30:00" (already converted to local time by backend)
      let date: Date;

      if (dateString.includes(' ') && !dateString.includes('T')) {
        // Backend format: "YYYY-MM-DD HH:mm:ss" (in local timezone - Asia/Kolkata)
        // Convert to ISO format for parsing
        // Since backend already converted to local time, we parse it as local time
        const isoString = dateString.replace(' ', 'T');
        date = new Date(isoString);
      } else if (dateString.includes('T')) {
        // ISO format - might have timezone info
        date = new Date(dateString);
      } else {
        // Fallback
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }

      // Display in user's local timezone (default: Asia/Kolkata)
      // Backend already converted UTC to local time, so we format it
      // Using 'en-IN' locale for Indian format
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
        // Note: Not specifying timeZone here since backend already sent local time
        // If backend sends UTC, we would use: timeZone: 'Asia/Kolkata'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-secondary-900">Notification Management</h1>
          <p className="text-secondary-600 mt-1 text-sm md:text-base">Manage and send notifications to users and businesses</p>
        </div>
        {activeTab === 'my-notifications' && notificationStats && (
          <div className="flex items-center space-x-3 md:space-x-4 w-full sm:w-auto">
            <div className="bg-primary-50 px-3 py-1.5 md:px-4 md:py-2 rounded-lg border border-primary-200">
              <div className="text-xs md:text-sm text-primary-700 font-medium">Unread</div>
              <div className="text-xl md:text-2xl font-bold text-primary-600">{notificationStats.unread_count}</div>
            </div>
            <button
              onClick={() => {
                fetchNotifications();
                fetchNotificationStats();
              }}
              className="flex items-center space-x-2 px-3 py-2 md:px-4 md:py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors"
              title="Refresh notifications"
            >
              <RefreshCw size={18} />
              <span className="hidden lg:inline">Refresh</span>
            </button>
            {notificationStats.unread_count > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center space-x-2 px-3 py-2 md:px-4 md:py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <CheckCheck size={18} />
                <span className="text-sm md:text-base">Mark All Read</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="flex border-b border-secondary-200">
          <button
            onClick={() => setActiveTab('my-notifications')}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${activeTab === 'my-notifications'
              ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50'
              : 'text-secondary-600 hover:text-primary-600 hover:bg-secondary-50'
              }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Bell size={18} />
              <span>My Notifications</span>
              {notificationStats && notificationStats.unread_count > 0 && (
                <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {notificationStats.unread_count}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('send')}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${activeTab === 'send'
              ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50'
              : 'text-secondary-600 hover:text-primary-600 hover:bg-secondary-50'
              }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Send size={18} />
              <span>Send Notification</span>
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'my-notifications' && (
            <div className="space-y-4">
              {/* Stats Cards */}
              {notificationStats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white p-4 md:p-5 rounded-xl shadow-sm border border-secondary-100 flex items-center justify-between hover:shadow-md transition-all">
                    <div>
                      <p className="text-secondary-500 text-sm font-medium mb-1">Payment</p>
                      <p className="text-3xl font-bold text-secondary-900">{notificationStats.total_by_type.payment}</p>
                    </div>
                    <div className="bg-blue-500 p-3 rounded-full shadow-lg shadow-blue-100">
                      <CreditCard className="text-white" size={24} />
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl shadow-sm border border-secondary-100 flex items-center justify-between hover:shadow-md transition-all">
                    <div>
                      <p className="text-secondary-500 text-sm font-medium mb-1">Business</p>
                      <p className="text-3xl font-bold text-secondary-900">{notificationStats.total_by_type.business}</p>
                    </div>
                    <div className="bg-emerald-500 p-3 rounded-full shadow-lg shadow-emerald-100">
                      <Store className="text-white" size={24} />
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl shadow-sm border border-secondary-100 flex items-center justify-between hover:shadow-md transition-all">
                    <div>
                      <p className="text-secondary-500 text-sm font-medium mb-1">Offer</p>
                      <p className="text-3xl font-bold text-secondary-900">{notificationStats.total_by_type.offer}</p>
                    </div>
                    <div className="bg-amber-500 p-3 rounded-full shadow-lg shadow-amber-100">
                      <Tag className="text-white" size={24} />
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-xl shadow-sm border border-secondary-100 flex items-center justify-between hover:shadow-md transition-all">
                    <div>
                      <p className="text-secondary-500 text-sm font-medium mb-1">System</p>
                      <p className="text-3xl font-bold text-secondary-900">{notificationStats.total_by_type.system}</p>
                    </div>
                    <div className="bg-rose-500 p-3 rounded-full shadow-lg shadow-rose-100">
                      <ShieldAlert className="text-white" size={24} />
                    </div>
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="flex items-center space-x-4">
                <Filter size={18} className="text-secondary-600" />
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value as NotificationType);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  className="px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Types</option>
                  <option value="payment">Payment</option>
                  <option value="business">Business</option>
                  <option value="offer">Offer</option>
                  <option value="system">System</option>
                </select>
              </div>

              {/* Notifications List */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : !notifications || notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell size={48} className="mx-auto text-secondary-400 mb-4" />
                  <p className="text-secondary-600">No notifications found</p>
                  <p className="text-sm text-secondary-500 mt-2">
                    {filterType !== 'all'
                      ? `No ${filterType} notifications found. Try selecting "All Types".`
                      : 'You don\'t have any notifications yet'}
                  </p>
                  {filterType !== 'all' && (
                    <button
                      onClick={() => setFilterType('all')}
                      className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      Show All Notifications
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-secondary-600 mb-2">
                    Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                    {filterType !== 'all' && ` (${filterType} type)`}
                  </div>
                  {notifications.map((notification) => {
                    console.log('Rendering notification:', notification);
                    return (
                      <div
                        key={notification.notification_id}
                        className={`p-4 rounded-lg border transition-all ${notification.is_read === 0
                          ? 'bg-primary-50 border-primary-200 shadow-sm'
                          : 'bg-white border-secondary-200 hover:border-secondary-300'
                          }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <h3 className="font-semibold text-secondary-900 text-base">{notification.title || 'Notification'}</h3>
                              {notification.is_read === 0 && (
                                <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                  New
                                </span>
                              )}
                              {notification.data && notification.data.type && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium capitalize">
                                  {notification.data.type}
                                </span>
                              )}
                            </div>
                            <p className="text-secondary-600 mb-3 text-sm leading-relaxed break-words">
                              {notification.message || 'No message'}
                            </p>
                            <div className="flex items-center flex-wrap gap-4 text-xs text-secondary-500">
                              <span className="flex items-center gap-1">
                                <span>📅</span>
                                <span>{formatDate(notification.created_at)}</span>
                              </span>
                              {notification.read_at && (
                                <span className="flex items-center gap-1">
                                  <span>✓</span>
                                  <span>Read {getTimeAgo(notification.read_at)}</span>
                                </span>
                              )}
                              {!notification.read_at && notification.is_read === 0 && (
                                <span className="text-primary-600 font-medium">Unread</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {notification.is_read === 0 && (
                              <button
                                onClick={() => handleMarkAsRead(notification.notification_id)}
                                className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                                title="Mark as read"
                              >
                                <Check size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification.notification_id)}
                              className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                              title="Delete notification"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-secondary-200">
                  <div className="text-sm text-secondary-600">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'send' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                  <div className="text-sm text-primary-700 font-medium mb-1">Total Sent</div>
                  <div className="text-2xl font-bold text-primary-600">{sendStats.total_sent}</div>
                  <div className="text-xs text-primary-600 mt-1">Notifications delivered</div>
                </div>
                <div className="bg-warning-50 p-4 rounded-lg border border-warning-200">
                  <div className="text-sm text-warning-700 font-medium mb-1">Scheduled</div>
                  <div className="text-2xl font-bold text-warning-600">{sendStats.total_scheduled}</div>
                  <div className="text-xs text-warning-600 mt-1">Pending notifications</div>
                </div>
                <div className="bg-danger-50 p-4 rounded-lg border border-danger-200">
                  <div className="text-sm text-danger-700 font-medium mb-1">Failed</div>
                  <div className="text-2xl font-bold text-danger-600">{sendStats.total_failed}</div>
                  <div className="text-xs text-danger-600 mt-1">Failed deliveries</div>
                </div>
                <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                  <div className="text-sm text-secondary-700 font-medium mb-1">Total</div>
                  <div className="text-2xl font-bold text-secondary-600">{sendStats.total_notifications}</div>
                  <div className="text-xs text-secondary-600 mt-1">All notifications</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowSendModal(true)}
                  className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <Send size={18} />
                  <span>Send Notification</span>
                </button>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors"
                >
                  <Calendar size={18} />
                  <span>Schedule Notification</span>
                </button>
              </div>

              {/* Search and Filters */}
              <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={18} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSentPagination({ ...sentPagination, page: 1 });
                      }}
                      placeholder="Search by title or message..."
                      className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'sent' | 'scheduled');
                      setSentPagination({ ...sentPagination, page: 1 });
                    }}
                    className="px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Status</option>
                    <option value="sent">Sent</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                  <select
                    value={recipientFilter}
                    onChange={(e) => {
                      setRecipientFilter(e.target.value as 'all' | 'All' | 'User' | 'Business');
                      setSentPagination({ ...sentPagination, page: 1 });
                    }}
                    className="px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Recipients</option>
                    <option value="All">All (Users & Businesses)</option>
                    <option value="User">Users Only</option>
                    <option value="Business">Businesses Only</option>
                  </select>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setRecipientFilter('all');
                      setSentPagination({ ...sentPagination, page: 1 });
                    }}
                    className="flex items-center justify-center space-x-2 px-4 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
                  >
                    <RefreshCw size={18} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>

              {/* Sent Notifications Table */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : sentNotifications.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-secondary-200">
                  <Send size={48} className="mx-auto text-secondary-400 mb-4" />
                  <p className="text-secondary-600">No notifications found</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50 border-b border-secondary-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Message
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Recipient
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Schedule/Sent At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Sent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Failed
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-secondary-200">
                        {sentNotifications.map((notification) => (
                          <tr key={notification.notification_id} className="hover:bg-secondary-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-secondary-900">{notification.title}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-secondary-600 max-w-xs truncate" title={notification.message}>
                                {notification.message}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                                {notification.notification_for}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${notification.status === 'sent'
                                ? 'bg-success-100 text-success-700'
                                : 'bg-warning-100 text-warning-700'
                                }`}>
                                {notification.status === 'sent' ? 'Sent' : 'Scheduled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-secondary-600">
                                {notification.status === 'sent' && notification.sent_at
                                  ? formatDate(notification.sent_at)
                                  : notification.status === 'scheduled'
                                    ? formatDate(notification.schedule_at)
                                    : formatDate(notification.created_at)
                                }
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-success-600">
                                {notification.status === 'sent' ? notification.sent_count : '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-danger-600">
                                {notification.status === 'sent' ? notification.failed_count : '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${notification.notification_type === 'Immediate'
                                ? 'bg-success-100 text-success-700'
                                : 'bg-warning-100 text-warning-700'
                                }`}>
                                {notification.notification_type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {sentPagination.totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-secondary-200 bg-secondary-50">
                      <div className="text-sm text-secondary-600">
                        Showing {((sentPagination.page - 1) * sentPagination.limit) + 1} to {Math.min(sentPagination.page * sentPagination.limit, sentPagination.total)} of {sentPagination.total} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSentPagination({ ...sentPagination, page: sentPagination.page - 1 })}
                          disabled={sentPagination.page === 1}
                          className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
                        >
                          Previous
                        </button>
                        <div className="px-4 py-2 text-sm text-secondary-600">
                          Page {sentPagination.page} of {sentPagination.totalPages}
                        </div>
                        <button
                          onClick={() => setSentPagination({ ...sentPagination, page: sentPagination.page + 1 })}
                          disabled={sentPagination.page === sentPagination.totalPages}
                          className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Send Notification Modal */}
          {showSendModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-secondary-900">Send Notification</h3>
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X size={24} />
                  </button>
                </div>
                <form onSubmit={handleSendNotification} className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={sendForm.title}
                      onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter notification title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={sendForm.message}
                      onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                      required
                      rows={5}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter notification message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Send To *
                    </label>
                    <select
                      value={sendForm.notification_for}
                      onChange={(e) => setSendForm({ ...sendForm, notification_for: e.target.value as 'All' | 'User' | 'Business' })}
                      required
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="All">All (Users & Businesses)</option>
                      <option value="User">Users Only</option>
                      <option value="Business">Businesses Only</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowSendModal(false)}
                      className="flex-1 px-6 py-3 bg-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="animate-spin" size={18} />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          <span>Send Notification</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Schedule Notification Modal */}
          {showScheduleModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-secondary-900">Schedule Notification</h3>
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X size={24} />
                  </button>
                </div>
                <form onSubmit={handleScheduleNotification} className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={scheduleForm.title}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, title: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter notification title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Send To *
                      </label>
                      <select
                        value={scheduleForm.notification_for}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, notification_for: e.target.value as 'All' | 'User' | 'Business' })}
                        required
                        className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All (Users & Businesses)</option>
                        <option value="User">Users Only</option>
                        <option value="Business">Businesses Only</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={scheduleForm.message}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, message: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter notification message"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Schedule Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleForm.schedule_at}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_at: e.target.value })}
                      required
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="flex items-center space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowScheduleModal(false)}
                      className="flex-1 px-6 py-3 bg-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-warning-500 text-white rounded-lg hover:bg-warning-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="animate-spin" size={18} />
                          <span>Scheduling...</span>
                        </>
                      ) : (
                        <>
                          <Calendar size={18} />
                          <span>Schedule Notification</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                <div className="p-6 border-b border-secondary-200 flex items-center justify-between bg-secondary-50">
                  <h3 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
                    <Trash2 className="text-danger-500" size={20} />
                    Delete Notification
                  </h3>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="text-secondary-400 hover:text-secondary-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-secondary-600 mb-6">
                    Are you sure you want to delete this notification? This action cannot be undone.
                  </p>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteNotification}
                      className="flex-1 px-4 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <Trash2 size={18} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default NotificationManagement;

